/**
 * Model-level undo for the deck template editor. Every edit replaces the whole (immutable) deck
 * model, so undo survives switching slides and inspector tabs. Typing in one input is merged
 * into one step: edits that carry the same `coalesce` key within COALESCE_MS replace the last
 * step instead of adding one.
 */
import { useCallback, useMemo, useReducer } from 'react';

/** Most undo steps kept. */
export const HISTORY_LIMIT = 100;

/** Edits to the same input closer than this are one undo step (ms). */
export const COALESCE_MS = 1200;

/**
 * Fresh history.
 * @param {object} model - Current model.
 * @returns {{past: object[], present: object, future: object[], lastKey: string|null, lastAt: number}} History.
 */
export function createHistory(model) {
  return { past: [], present: model, future: [], lastKey: null, lastAt: 0 };
}

/**
 * Apply an edit.
 * @param {object} history - History.
 * @param {object} next - New model.
 * @param {{coalesce?: string|null, now?: number}} [options] - Merge key and time.
 * @returns {object} History.
 */
export function pushHistory(history, next, { coalesce = null, now = Date.now() } = {}) {
  if (next === history.present) return history;
  const merge = coalesce && history.lastKey === coalesce && now - history.lastAt < COALESCE_MS && history.past.length > 0;
  const past = merge ? history.past : [...history.past, history.present].slice(-HISTORY_LIMIT);
  return { past, present: next, future: [], lastKey: coalesce, lastAt: now };
}

/**
 * Undo one step.
 * @param {object} history - History.
 * @returns {object} History.
 */
export function undoHistory(history) {
  if (history.past.length === 0) return history;
  const previous = history.past[history.past.length - 1];
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future], lastKey: null, lastAt: 0 };
}

/**
 * Redo one step.
 * @param {object} history - History.
 * @returns {object} History.
 */
export function redoHistory(history) {
  if (history.future.length === 0) return history;
  const [next, ...rest] = history.future;
  return { past: [...history.past, history.present], present: next, future: rest, lastKey: null, lastAt: 0 };
}

/**
 * Reducer for {@link useDeckHistory}.
 * @param {object} history - History.
 * @param {object} action - `{type: 'apply', update, coalesce}` | `{type: 'undo'}` | `{type: 'redo'}` | `{type: 'reset', model}`.
 * @returns {object} History.
 */
export function historyReducer(history, action) {
  switch (action.type) {
    case 'apply': {
      const next = typeof action.update === 'function' ? action.update(history.present) : action.update;
      if (!next) return history;
      return pushHistory(history, next, { coalesce: action.coalesce, now: action.now });
    }
    case 'undo':
      return undoHistory(history);
    case 'redo':
      return redoHistory(history);
    case 'reset':
      return createHistory(action.model);
    default:
      return history;
  }
}

/**
 * Deck model state with undo and redo.
 * @param {object} initialModel - Model to start from.
 * @returns {{model: object, apply: function(function(object): object, string=): void, undo: function(): void, redo: function(): void, reset: function(object): void, canUndo: boolean, canRedo: boolean}} Editor state.
 */
export function useDeckHistory(initialModel) {
  const [history, dispatch] = useReducer(historyReducer, initialModel, createHistory);
  const apply = useCallback((update, coalesce = null) => dispatch({ type: 'apply', update, coalesce, now: Date.now() }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const reset = useCallback((model) => dispatch({ type: 'reset', model }), []);
  return useMemo(() => ({
    model: history.present,
    apply,
    undo,
    redo,
    reset,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  }), [history, apply, undo, redo, reset]);
}
